import {Person, PersonEntity, PersonType, toPersonType} from "../../../model/person";
import {contains} from "../../../util/util";
import {createParamSelector, getUseQueryResultData} from "../../../util/selector-util";
import {createSelector} from "@reduxjs/toolkit";
import {PersonState, PersonStateEntity} from "../../../model/person-state";
import {PersonCapability, PersonCapabilityEntity} from "../../../model/person-capability";

export type PersonSelectorProps = {
  id?: string
  regionId?: string
  companyId?: string;
  personType?: PersonType;
  showDoNotUse?: boolean;
  active?: boolean
};

const getIdParam = createParamSelector<PersonSelectorProps | undefined, string | undefined>(params => params?.id);
const getRegionIdParam = createParamSelector<PersonSelectorProps | undefined, string | undefined>(params => params?.regionId);
const getCompanyIdParam = createParamSelector<PersonSelectorProps | undefined, string | undefined>(params => params?.companyId);
const getPersonTypeCodeParam = createParamSelector<PersonSelectorProps | undefined, string | undefined>(params => params?.personType?.code);
const getShowDoNotUseParam = createParamSelector<PersonSelectorProps | undefined, boolean | undefined>(params => params?.showDoNotUse);
const getActiveParam = createParamSelector<PersonSelectorProps | undefined, boolean | undefined>(params => params?.active);

export const personsSelector = () => createSelector(
  [
    getUseQueryResultData<Person[]>,
    getIdParam,
    getRegionIdParam,
    getCompanyIdParam,
    getPersonTypeCodeParam,
    getShowDoNotUseParam,
    getActiveParam,
  ],
  (
    data,
    id,
    regionId,
    companyId,
    personTypeCode,
    showDoNotUse,
    active,
  ) => {
    return selectPersonsAndTransform(
      data ?? [], {
        id: id,
        regionId: regionId,
        companyId: companyId,
        personType: personTypeCode ? toPersonType(personTypeCode) : undefined,
        showDoNotUse: showDoNotUse,
        active: active,
      });
  }
);

export const personSelector = () => createSelector(
  [getUseQueryResultData<Person>],
  (data) => {
    return data ? transformPerson(data) : undefined;
  }
);

export const personStateSelector = () => createSelector(
  [getUseQueryResultData<PersonState>],
  (data) => {
    return data ? transformPersonState(data) : undefined;
  }
);

export const selectPersons = (persons?: Person[], selector?: PersonSelectorProps): Person[] => {
  if (!persons) {
    return [];
  }

  return persons.filter((p) => {
    let filterIn = true;

    if (selector?.id) {
      if (selector.id !== p.id) {
        filterIn = false;
      }
    }

    if (selector?.regionId) {
      if (!contains(p.regionList, id => id === selector.regionId)) {
        filterIn = false;
      }
    }

    if (selector?.companyId) {
      if (selector.companyId !== p.companyId) {
        filterIn = false;
      }
    }

    if (filterIn && selector?.personType) {
      if (selector.personType.code !== p.type) {
        filterIn = false;
      }
    }

    const showDoNotUse = selector?.showDoNotUse ?? false;
    if (filterIn && !showDoNotUse && p.doNotUse) {
      filterIn = false;
    }

    const active = selector?.active
    if (typeof active === "undefined") {
      if (filterIn && p.active === active) {
        filterIn = false;
      }
    }

    return filterIn;
  });
};

export const transformPerson = (value: Person): PersonEntity => {
  return PersonEntity.from(value)
}

export const selectPersonsAndTransform = (values?: Person[], selector?: PersonSelectorProps): PersonEntity[] => {
  const selected = selectPersons(values, selector);
  return selected.map(transformPerson);
};

export const transformPersonState = (value: PersonState): PersonStateEntity => {
  return PersonStateEntity.from(value)
}

export type PersonCapabilitySelectorProps = {};

export const personCapabilitiesSelector = () => createSelector(
  [getUseQueryResultData<PersonCapability[]>],
  (data) => {
    return selectPersonCapabilitiesAndTransform(
      data ?? [], {}
    );
  }
);

export const personCapabilityTypesSelector = () => createSelector(
  [getUseQueryResultData<PersonCapability[]>],
  (data) => {
    const caps = selectPersonCapabilitiesAndTransform(data ?? [], {});
    return new Set(caps.map(c => c.capability))
  }
);

export const personCapabilitySelector = () => createSelector(
  [getUseQueryResultData<PersonCapability>],
  (data) => {
    return data ? transformPersonCapability(data) : undefined;
  }
);

export const selectPersonCapabilities = (values?: PersonCapability[], selector?: PersonCapabilitySelectorProps): PersonCapability[] => {
  if (!values) {
    return [];
  }

  return values.filter((v) => {
    let filterIn = true;
    return filterIn;
  });
};

export const transformPersonCapability = (value: PersonCapability): PersonCapabilityEntity => {
  return PersonCapabilityEntity.from(value)
}

export const selectPersonCapabilitiesAndTransform = (values?: PersonCapability[], selector?: PersonCapabilitySelectorProps): PersonCapabilityEntity[] => {
  const selected = selectPersonCapabilities(values, selector);
  return selected.map(transformPersonCapability);
};
